import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AUTH_ERROR_STATUS } from '@app/core/enums/auth-error-status';
import { HTTPRequestMethod } from '@app/core/enums/http-request-method.enum';
import { MimeType } from '@app/core/enums/mime-type.enum';
import { ClaimService } from '@services/claims/claims.service';
import { StorageService } from '@services/storage/storage.service';
import { LoggerService } from '@services/logger/logger.service';
import { Observable, Subject, throwError } from 'rxjs';
import { retryWhen, switchMap, take, timeout } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';


@Injectable({
  providedIn: 'root',
})
export class ApiSendRequestService {

  public onBehalfOf: string;
  public decodedUserIdToken: any;
  public userIdToken: string;
  private userTokenUpdatedSubject = new Subject<void>();

  /* istanbul ignore next */
  private readonly userIdTokenKey = environment.production ? 'user-id-token' : 'user-id-token-dev';

  constructor(private claimService: ClaimService,
    private http: HttpClient,
    private loggerService: LoggerService,
    private router: Router,
    private storageService: StorageService,
  ) {
  }

  public async getUserIdToken() {
    if (!this.userIdToken) {
      this.userIdToken = (await this.storageService.getSession(this.userIdTokenKey));
    }

    return this.userIdToken;
  }

  /* istanbul ignore next */
  public getUserTokenUpdatedObservable() {
    const tokenTimeout = 300000;

    return this.userTokenUpdatedSubject.asObservable().pipe(
      take(1),
      timeout(tokenTimeout),
    );
  }

  /**
   * @param  {string} path
   * @param  {HTTPRequestMethod} requestMethod
   * @param  {MimeType} mimeType?
   * @param  {any} body?
   * @returns Observable
   */
  public sendRequest(path: string, requestMethod: HTTPRequestMethod, mimeType?: MimeType, body?: any, isCacheable?: any): Observable<any> {
    const retryTimeout = 60000;

    /* istanbul ignore if */
    if (body) {
      console.log('send request with body ' + JSON.stringify(body));
    }

       return new Observable<any>((observer) => {
      (async () => {
      let claim = '';
      const header: any = {};
      if (mimeType) {
        header['Content-Type'] = mimeType;
      }

      /* istanbul ignore if */
      if (await this.getUserIdToken() == null) {
        claim = await this.claimService.getTokenClaims();
      } else {
        claim = await this.getUserIdToken();
      }

      header.Authorization = `Bearer ${claim}`;

      const httpOptions = {
        headers: new HttpHeaders(header),
      };

      const url = path;

      let request: any;

      switch (requestMethod) {
        case HTTPRequestMethod.Get: {

          /* istanbul ignore next */
          if (isCacheable) {
            const cacheData = JSON.parse(this.storageService.getSession(url));

            if (cacheData) {
              const now = new Date();
              const cacheExpired = (now.getTime() - new Date(cacheData.createDate).getTime()) >= environment.sessionExp;
              this.loggerService.log(`API cache for: ${url} expired: ${cacheExpired}`);

              if (!cacheExpired) {
                this.loggerService.log(`API cache response for ${path}: ${JSON.stringify(cacheData.response)}`);

                observer.next(cacheData.response);
                observer.complete();

                return cacheData.response;
              } else {
                this.storageService.removeSession(url);
              }
            }
          }
          request = this.http.get(url, httpOptions);
          break;
        }
        case HTTPRequestMethod.Post: {
          request = this.http.post(url, body, httpOptions);
          break;
        }
        case HTTPRequestMethod.Put: {
          request = this.http.put(url, body, httpOptions);
          break;
        }
        case HTTPRequestMethod.Delete: {
          request = this.http.delete(url, httpOptions);
          break;
        }
        /* istanbul ignore next */
        default: {
          break;
        }
      }

      /* istanbul ignore next */
      request.subscribe((response) => {
        if (isCacheable) {
          const data = { createDate: new Date(), response: response };
          this.storageService.setSession(url, JSON.stringify(data));
        }
        // enable for debugging
        // this.loggerService.log(`API response for ${path}: ${JSON.stringify(response)}`);

        observer.next(response);
        observer.complete();
      }, (error: any) => {
        this.loggerService.error(`API error for ${path}: + ${JSON.stringify(error)}`);
        observer.error(error);
      });
      })().catch((err) => observer.error(err));
    }).pipe(
      retryWhen((errors) => errors.pipe(
        switchMap((err) => {
          /* istanbul ignore if */
          if (err.status) {
            if (err.status === -1) {
              return this.getUserTokenUpdatedObservable();
            }

            if (err.status === AUTH_ERROR_STATUS.UNAUTHORIZED) {
              this.router.navigateByUrl(`/access-denied`);
            }
          }
          this.loggerService.error(`API error for ${err.url}: + ${JSON.stringify(err)}`);

          return throwError(err);
        }),
      )),
      timeout(retryTimeout),
    );
  }
}
